/**
 * Typed client for the design-revamp backend (Phase C).
 *
 * One source of truth for the new endpoints' request/response shapes so
 * the screen features don't each re-guess them. IMPORTANT: the API
 * serialises every Numeric column (prices, quantities, areas, budgets)
 * as a JSON **string** (Decimal → str). All money/qty/area fields below
 * are typed `string` — parse with `parseDecimal` from `@/lib/format`
 * before doing math or formatting.
 *
 * Fetchers are thin wrappers over `apiClient` (in-memory JWT, idempotency
 * keys on mutations, 401→refresh→retry already handled there). Features
 * wrap these in TanStack Query hooks; suggested query keys are noted.
 */
import { apiClient } from "@/lib/api-client";
import type { SupportedLanguage } from "@/lib/i18n";
import type { CalculatorSpec } from "@/features/chat/calculator-types";

// ─────────────────────────── shared ───────────────────────────

/** Per-language JSONB bag returned for material names/units/warnings. */
export interface LocalizedText {
  ru: string;
  ky: string;
  en: string;
}

export type MaterialConfidence = "high" | "med" | "low";

/** Pick the localized string with RU fallback (REQ-PRD-§13.2). */
export function pickLocalized(
  text: LocalizedText | null | undefined,
  lang: string,
): string {
  if (!text) return "";
  if (lang === "ky") return text.ky || text.ru;
  if (lang === "en") return text.en || text.ru;
  return text.ru;
}

// ─────────────────────────── quotes (Смета → materials) ───────────────────────────

export interface SellerMatch {
  seller_id: string;
  business_name: string;
  /** Decimal → string. */
  price_kgs: string;
  cheapest: boolean;
}

export interface MaterialItem {
  id: string;
  line_no: number;
  category_id: string | null;
  names: LocalizedText;
  unit: LocalizedText;
  sub: LocalizedText | null;
  warning: LocalizedText | null;
  /** Decimal → string. */
  qty: string;
  confidence: MaterialConfidence;
  matches: SellerMatch[];
  /** Backend capability C3. Absent today — see the backend capabilities spec. */
  calculator?: CalculatorSpec | null;
}

export interface QuoteSummary {
  positions: number;
  matched_positions: number;
  pending_positions: number;
  /** Decimal → string. */
  min_total_kgs: string;
  /** Decimal → string. */
  est_kgs: string;
}

export type QuoteStatus = "extracting" | "ready" | "failed";

export interface QuoteResponse {
  id: string;
  project_id: string | null;
  upload_id: string | null;
  status: QuoteStatus;
  quote_number: string | null;
  contractor_name: string | null;
  quote_date: string | null;
  materials: MaterialItem[];
  summary: QuoteSummary;
}

export interface QuoteCreateRequest {
  project_id?: string;
  upload_id?: string;
}

/** GET /quotes/{id}. queryKey: ['quote', id]. */
export function getQuote(quoteId: string): Promise<QuoteResponse> {
  return apiClient.get<QuoteResponse>(`/quotes/${quoteId}`);
}

/**
 * POST /quotes — extracts the seeded Смета №142 (demo). Cache the result
 * (queryKey ['quote', 'current'], long staleTime) so a session creates
 * exactly one. Returns the full materials+matches+summary payload incl. id.
 */
export function createQuote(body: QuoteCreateRequest = {}): Promise<QuoteResponse> {
  return apiClient.post<QuoteResponse, QuoteCreateRequest>("/quotes", body);
}

// ─────────────────────────── projects ───────────────────────────

export type HousingType = "khrushchevka" | "new_build" | "private_house";
export type ProjectCity = "bishkek" | "osh";
export type ProjectTimeline = "this_month" | "1_3_months" | "later";

export interface ProjectResponse {
  id: string;
  title: string;
  /** Decimal → string. */
  area_m2: string | null;
  housing_type: HousingType | null;
  district: string | null;
  city: ProjectCity | null;
  /** Decimal → string. */
  budget_kgs: string | null;
  timeline: ProjectTimeline | null;
}

export interface ProjectListResponse {
  projects: ProjectResponse[];
}

export interface ProjectCreateRequest {
  title: string;
  area_m2?: number | string | null;
  housing_type?: HousingType | null;
  district?: string | null;
  city?: ProjectCity | null;
  budget_kgs?: number | string | null;
  timeline?: ProjectTimeline | null;
}

/** GET /projects. queryKey: ['projects']. */
export function listProjects(): Promise<ProjectListResponse> {
  return apiClient.get<ProjectListResponse>("/projects");
}

/** GET /projects/{id}. queryKey: ['project', id]. */
export function getProject(projectId: string): Promise<ProjectResponse> {
  return apiClient.get<ProjectResponse>(`/projects/${projectId}`);
}

/** POST /projects (idempotent, 201). */
export function createProject(body: ProjectCreateRequest): Promise<ProjectResponse> {
  return apiClient.post<ProjectResponse, ProjectCreateRequest>("/projects", body);
}

// ─────────────────────────── sellers (finder) ───────────────────────────

export interface SellerSummary {
  id: string;
  business_name: string;
  market_id: string;
  pavilion_number: string;
  /** Seller business contact, exposed on the finder for the Shops Call/WhatsApp
   * CTAs (SCRUM-134). A shop's public number, not personal PII. */
  phone_e164: string;
  whatsapp_phone_e164: string | null;
  /** Decimal → string. */
  lat: string | null;
  /** Decimal → string. */
  lng: string | null;
  market_name_ru: string;
  market_name_ky: string | null;
  /** Decimal → string. */
  rating: string | null;
  reviews_count: number;
  years_in_business: number | null;
  languages: string[];
  warranty: boolean;
  operating_hours: string | null;
  positions_count: number;
  district: string | null;
  market_label: string | null;
}

export interface SellerSummaryListResponse {
  sellers: SellerSummary[];
  total: number;
}

export interface SellerFinderParams {
  category?: string;
  district?: string;
  q?: string;
}

/** GET /marketplace/sellers (public). queryKey: ['sellers', params]. */
export function listSellers(
  params: SellerFinderParams = {},
): Promise<SellerSummaryListResponse> {
  const qs = new URLSearchParams();
  if (params.category) qs.set("category", params.category);
  if (params.district) qs.set("district", params.district);
  if (params.q) qs.set("q", params.q);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiClient.get<SellerSummaryListResponse>(`/marketplace/sellers${suffix}`);
}

// ─────────────────────────── knowledge base ───────────────────────────

export interface KbHeadingBlock {
  h: string;
  p: string;
}
export interface KbCalloutBlock {
  callout: "info" | "warn";
  text: string;
}
export type KbBlock = KbHeadingBlock | KbCalloutBlock;

export function isCalloutBlock(b: KbBlock): b is KbCalloutBlock {
  return "callout" in b;
}

export interface KbArticleSummary {
  slug: string;
  title: string;
  lang: string;
  reading_time_min: number | null;
}

export interface KbArticleDetail {
  slug: string;
  lang: string;
  title: string;
  toc: string[];
  body: KbBlock[];
  reading_time_min: number | null;
}

export interface KbArticleListResponse {
  articles: KbArticleSummary[];
}

/** GET /kb/articles?lang=. queryKey: ['kb','articles', lang]. */
export function listArticles(lang: SupportedLanguage): Promise<KbArticleListResponse> {
  return apiClient.get<KbArticleListResponse>(
    `/kb/articles?lang=${encodeURIComponent(lang)}`,
  );
}

/** GET /kb/articles/{slug}?lang= (RU fallback server-side). queryKey: ['kb','article', slug, lang]. */
export function getArticle(
  slug: string,
  lang: SupportedLanguage,
): Promise<KbArticleDetail> {
  return apiClient.get<KbArticleDetail>(
    `/kb/articles/${encodeURIComponent(slug)}?lang=${encodeURIComponent(lang)}`,
  );
}

// ─────────────────────────── phone-first signup ───────────────────────────

export interface PhoneSignupStartRequest {
  phone_e164: string;
}
export interface PhoneSignupStartResponse {
  phone_pending_verification: boolean;
  throttle_until: string;
  /**
   * NON-PRODUCTION ONLY. In a QA/local environment without real SMS delivery
   * the backend echoes the freshly issued plaintext OTP here so a tester can
   * complete phone verification on-screen. Always `null` in production.
   */
  dev_otp?: string | null;
}
export interface PhoneSignupVerifyRequest {
  phone_e164: string;
  code: string;
}
/** Mirrors the login response; the access token is set into auth-store by the caller. */
export interface PhoneSignupVerifyResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: { id: string; email: string; language: SupportedLanguage };
}

/** POST /auth/phone-signup/start (idempotent). */
export function phoneSignupStart(
  body: PhoneSignupStartRequest,
): Promise<PhoneSignupStartResponse> {
  return apiClient.post<PhoneSignupStartResponse, PhoneSignupStartRequest>(
    "/auth/phone-signup/start",
    body,
  );
}

/** POST /auth/phone-signup/verify (idempotent) → session (sets garden_rt cookie). */
export function phoneSignupVerify(
  body: PhoneSignupVerifyRequest,
): Promise<PhoneSignupVerifyResponse> {
  return apiClient.post<PhoneSignupVerifyResponse, PhoneSignupVerifyRequest>(
    "/auth/phone-signup/verify",
    body,
  );
}

// ─────────────────────────── email signup ───────────────────────────

export interface EmailSignupRequest {
  email: string;
  password: string;
  language: SupportedLanguage;
}
export interface EmailSignupResponse {
  user_id: string;
  email: string;
  email_pending_verification: boolean;
  /**
   * NON-PRODUCTION ONLY. In a QA/local environment without real email (SES)
   * delivery the backend echoes the freshly issued plaintext email OTP here so
   * a tester can complete verification on-screen. Always `null` in production.
   */
  dev_otp?: string | null;
}
export interface EmailVerifyRequest {
  user_id: string;
  otp: string;
}
export interface EmailVerifyResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  redirect: string;
}

/** POST /auth/signup (idempotent) → new user_id + (non-prod) dev_otp. */
export function emailSignup(
  body: EmailSignupRequest,
): Promise<EmailSignupResponse> {
  return apiClient.post<EmailSignupResponse, EmailSignupRequest>(
    "/auth/signup",
    body,
  );
}

/** POST /auth/email/verify (idempotent) → session (sets garden_rt cookie). */
export function emailVerify(
  body: EmailVerifyRequest,
): Promise<EmailVerifyResponse> {
  return apiClient.post<EmailVerifyResponse, EmailVerifyRequest>(
    "/auth/email/verify",
    body,
  );
}

// --- Chat answer feedback (Phase 5) ----------------------------------------

/** Thumbs rating on an assistant turn. */
export type ChatFeedbackRating = "up" | "down";

interface ChatFeedbackRequest {
  message_id: string;
  rating: ChatFeedbackRating;
}

/**
 * Record a thumbs up/down on an assistant message. Upsert — one rating per
 * (user, message); re-posting flips it. Returns 204 (no body). The api-client
 * attaches the Bearer token + a per-attempt Idempotency-Key.
 */
export async function postChatFeedback(
  messageId: string,
  rating: ChatFeedbackRating,
): Promise<void> {
  await apiClient.post<void, ChatFeedbackRequest>(
    "/chat/feedback",
    { message_id: messageId, rating },
    // Guest-allowed route (contract): a guest's thumbs carry the guest token;
    // a member is unchanged (apiFetch prefers the member token).
    { allowGuest: true },
  );
}
